import { FC, ReactNode } from 'react';

import { Button } from '@bonusly/design-system';
import { useIsBizyEnabled } from '@bonusly/who-am-i';

import { Bizy } from '../bizy';
import { dispatchBizyOpenEvent } from './hooks/use-bizy-open-listener';

export type BizyPromptButtonProps = {
	/** Event source for analytics and backend context (e.g. 'admin_insight', 'awards_education'). */
	source: string;
	/** Passed to Bizy as page context (e.g. 'participation_report', 'awards_index'). */
	pageContext: string;
	/** Auto-sent starter message when the panel opens. */
	starterMessage: string;
	/** Button label shown next to the bee icon. @default 'Ask Bizy' */
	label?: string;
	/** Fully custom button content. When provided, overrides the default icon + label. */
	children?: ReactNode;
};

/**
 * Button that opens the Bizy side panel with a pre-seeded starter message.
 * Unstyled beyond a basic ghost button -- intended to be wrapped or styled
 * by the consumer when placing it on a specific page.
 *
 * Renders nothing when Bizy is disabled for the company.
 *
 * @example
 * <BizyPromptButton
 *   source="admin_insight"
 *   pageContext="participation_report"
 *   starterMessage="Tell me about my participation data."
 * />
 *
 * @example
 * <BizyPromptButton
 *   source="awards_education"
 *   pageContext="awards_index"
 *   starterMessage="Tell me about awards."
 * >
 *   Ask Bizy about awards
 * </BizyPromptButton>
 */
export const BizyPromptButton: FC<BizyPromptButtonProps> = ({ source, pageContext, starterMessage, label = 'Ask Bizy', children }) => {
	const isBizyEnabled = useIsBizyEnabled();

	if (!isBizyEnabled) {
		return null;
	}

	return (
		<Button
			variant="outline"
			size="sm"
			leftIcon={children ? undefined : <Bizy state="curious" size={24} freeze />}
			onClick={() =>
				dispatchBizyOpenEvent({
					source,
					pagePath: window.location.pathname,
					pageContext,
					starterMessage,
				})
			}
		>
			{children ?? label}
		</Button>
	);
};
